import {
  getData,
  updateData,
  removeData,
  setData,
  subscribeToData,
  uploadFile,
  updateMultipleSafe
} from './api-wrapper.service';
import { database, auth} from './core';
import { ref, get} from 'firebase/database';
import perksService from '../perks.service';
import { userAPIService } from '../api/user.service';
import { School } from '../../types/user';
// Notification creation is handled server-side by Cloud Functions
// (friendsChanged triggers on /users/{uid}/friends/{friendUid} writes)
import { calculateImageAspectRatio } from '../../utils/image-helpers';
import logger from '../../utils/logger';

// Types
export interface UserProfile {
  uid: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  bio?: string;
  photoURL?: string;
  profilePhotoURL?: string; // Legacy field name support
  coverPhotoURL?: string;
  coverPhoto?: { // Original app structure
    url: string;
    aspectRatio: number;
  };
  location?: string;
  dateCreated: number;
  dateModified?: number;
  isPeakSub: boolean;
  preferredWeightUM: 'kg' | 'lbs';
  isInstructor?: boolean;
  isAdmin?: boolean;
  
  // CRITICAL: Push notification support for social function compatibility
  pushToken?: string; // Used by Firebase social functions for push notifications
  
  // Age-related fields from original app
  age?: string; // Stored as string in original
  dateOfBirth?: number; // Timestamp
  dob?: string; // Date of birth as string (for form handling)
  gender?: number | null; // 0=Male, 1=Female, 2=Other (stored as number in original app)
  
  // Belt-related fields at root level (original app structure)
  beltDesignation?: string;
  stripesCount?: number;
  instructor?: string; // Instructor name
  school?: { // Academy/school info
    name?: string;
    instructor?: string;
    location?: string;
    placeId?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    lat?: number;
    lng?: number;
  } | School; // Support both inline and School interface
  
  // Social media - using original field name
  socialMediaProfiles?: {
    facebook?: string; // Full URL
    instagram?: string; // Username only
    twitter?: string; // Username only
    youtube?: string; // Username only
    website?: string; // Full URL
  };
  socialLinks?: { // Keep for backward compatibility
    instagram?: string;
    twitter?: string;
    facebook?: string;
    youtube?: string;
    website?: string;
  };
  
  // Trained martial arts - original app structure
  trainedStyles?: {
    primary: string;
    [key: string]: string;
  };
  trainedMartialArts?: string[]; // Array of martial art names
  belt?: Record<string, any>; // Belt data by martial art
  
  martialArts?: Record<string, {
    name: string;
    startDate: number;
    experience: number;
    rank?: string;
    instructor?: string;
  }>;
  // CRITICAL: Friend compatibility - support both Phoenix boolean and original app status formats
  friends?: Record<string, boolean | "accepted" | "request-sent" | "request-received" | "declined">;
  friendRequests?: Record<string, {
    from: string;
    date: number;
    status: 'pending' | 'accepted' | 'declined';
  }>;
  preferences?: {
    theme: 'light' | 'dark' | 'system';
    notifications: boolean;
    privacy: {
      profileVisibility: 'public' | 'friends' | 'private';
      showTrainingSessions: boolean;
      showCompetitions: boolean;
    };
  };
  stats?: {
    totalTrainings: number;
    totalTime: number;
    totalRollTime: number;
    competitions: number;
    promotions: number;
  };
  // Public stats (aggregated from training posts)
  public?: {
    posts?: {
      gi?: {
        trainings: { count: number; timeTrained: number; rollTime: number };
        seminars: { count: number; timeTrained: number; rollTime: number };
        camps: { count: number; timeTrained: number; rollTime: number };
        teachings: { count: number; timeTrained: number; rollTime: number };
      };
      noGi?: {
        trainings: { count: number; timeTrained: number; rollTime: number };
        seminars: { count: number; timeTrained: number; rollTime: number };
        camps: { count: number; timeTrained: number; rollTime: number };
        teachings: { count: number; timeTrained: number; rollTime: number };
      };
    };
  };
}

export interface UserSearchResult {
  uid: string;
  id?: string; // Add id field to ensure compatibility with both Firebase and API results
  username: string;
  displayName?: string;
  profilePhotoURL?: string | null; // Use profilePhotoURL to match original app field naming
  isInstructor?: boolean;
  isPeakSub?: boolean; // Peak subscription status
  exactMatch?: boolean; // Indicates if this is an exact match
  matchQuality?: number; // 3=exact, 2=prefix, 1=substring
  matchType?: 'exact' | 'prefix' | 'substring'; // Type of match for sorting
}

export interface UserProfileUpdateData {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  bio?: string;
  location?: string;
  isInstructor?: boolean;
  isPeakSub?: boolean; // Premium subscription status
  lastSubscriptionSync?: string; // ISO date string of last subscription sync
  
  // CRITICAL: Push notification support for social function compatibility
  pushToken?: string; // Used by Firebase social functions for push notifications
  
  // Age-related fields
  age?: string;
  dateOfBirth?: number;
  dob?: string; // Date of birth as string (for form handling)
  gender?: number | null; // Form should convert to number before saving
  
  // Belt/training fields at root level
  beltDesignation?: string;
  stripesCount?: number;
  instructor?: string;

  // Custom belt configuration
  customBelt?: {
    name: string;
    rank: string;
    level: 'adult' | 'kids';
    baseColor: string;
    contrastColor: string;
    contrastType: string;
    stripeCount: number;
    stripeColor: string;
    stripeBgColor: string;
    createdAt?: string;
  };
  school?: {
    name?: string;
    instructor?: string;
    location?: string;
    placeId?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    lat?: number;
    lng?: number;
  };
  
  // Cover photo with original structure
  coverPhoto?: {
    url: string;
    aspectRatio: number;
  };
  
  // Profile photo URL (legacy field)
  profilePhotoURL?: string;
  
  // Social media - original field name
  socialMediaProfiles?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    youtube?: string;
    website?: string;
  };
  socialLinks?: { // Keep for backward compatibility
    instagram?: string;
    twitter?: string;
    facebook?: string;
    youtube?: string;
    website?: string;
  };
  // Units/measurement preferences (matching original app)
  preferredWeightUM?: 'kg' | 'lb';
  dateFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  timeFormat?: '12h' | '24h';

  preferences?: {
    theme?: 'light' | 'dark' | 'system';
    notifications?: boolean;
    privacy?: {
      profileVisibility?: 'public' | 'friends' | 'private';
      showTrainingSessions?: boolean;
      showCompetitions?: boolean;
    };
  };
  // Legacy belt-related data from original Marune
  belt?: {
    designation?: string;
    beltDesignation?: string;
    baseColor?: string;
    color?: string;
    name?: string;
    stripes?: number;
    dateObtained?: number;
    promotedOn?: number;
    instructor?: string;
    promotionHistory?: Array<{
      id?: string;
      promotionId?: string;
      belt: string;
      date: number;
      academy?: string;
      instructor?: string;
    }>;
    history?: Record<string, any>;
    "Brazilian Jiu Jitsu"?: {
      designation?: string;
      name?: string;
      stripes?: number;
      dateObtained?: number;
    };
  };
}

// Get profile by user ID
// Options:
//   skipAdminCheck: Skip the API call for admin status (faster, use for friend suggestions etc.)
export const getProfileByUid = async (
  uid: string,
  options?: { skipAdminCheck?: boolean }
): Promise<UserProfile | null> => {
  try {
    const profile = await getData(`/users/${uid}`);
    if (!profile) {
      return null;
    }

    // Process profile data

    // Create a normalized profile object with enhanced field mapping
    // Only fetch admin status from API if not skipped (expensive call)
    let isAdminFromAPI = false;
    if (!options?.skipAdminCheck) {
      try {
        // Use the getUserProfile API which checks PostgreSQL
        logger.info(`[getProfileByUid] Fetching admin status from API for uid: ${uid}`);
        const apiProfile = await userAPIService.getUserProfile(uid);
        isAdminFromAPI = apiProfile.is_admin || false;
        logger.info(`[getProfileByUid] API returned is_admin: ${apiProfile.is_admin}, resolved to: ${isAdminFromAPI}`);
      } catch (error) {
        // Log API errors instead of silently swallowing them
        logger.error(`[getProfileByUid] Failed to fetch admin status from API:`, error);
      }
    }
    
    const normalizedProfile = {
      uid,
      ...profile,
      // Ensure the username field is present
      username: profile.username,
      // Ensure the isInstructor field is properly set
      isInstructor: profile.isInstructor === true,
      // Admin status only comes from PostgreSQL via API
      isAdmin: isAdminFromAPI,
      // Include Peak subscription status
      isPeakSub: profile.isPeakSub === true,
      // Handle both photoURL and profilePhotoURL
      photoURL: profile.photoURL || profile.profilePhotoURL,
      profilePhotoURL: profile.profilePhotoURL || profile.photoURL, // Support both field names
      // Handle coverPhoto structure
      coverPhoto: profile.coverPhoto || (profile.coverPhotoURL ? {
        url: profile.coverPhotoURL,
        aspectRatio: 1 // Default aspect ratio if not provided
      } : undefined),
      coverPhotoURL: profile.coverPhotoURL || profile.coverPhoto?.url,
      // Handle social media profiles - map both field names
      socialMediaProfiles: profile.socialMediaProfiles || profile.socialLinks,
      socialLinks: profile.socialLinks || profile.socialMediaProfiles,
      // Ensure root-level belt fields are present
      // Note: We'll override these if BJJ-specific data exists
      beltDesignation: profile.beltDesignation || profile.belt?.designation,
      stripesCount: profile.stripesCount !== undefined ? profile.stripesCount : (profile.belt?.stripes || 0),
      // IMPORTANT: Include instructor field from profile
      instructor: profile.instructor || profile.belt?.instructor || profile.belt?.['Brazilian Jiu Jitsu']?.instructor,
      // Include school data
      school: profile.school,
      // Make sure we have stats with correct structure
      stats: {
        totalTrainings: profile.stats?.totalTrainings || 0,
        totalTime: profile.stats?.totalTime || 0,
        totalRollTime: profile.stats?.totalRollTime || 0,
        competitions: profile.stats?.competitions || 0,
        promotions: profile.stats?.promotions || 0,
        // Additional stats fields from profile if they exist
        ...(profile.stats || {})
      }
    };
    
    // Handle legacy data structure for belt information
    if (profile.belt && profile.belt["Brazilian Jiu Jitsu"]) {
      const bjjBelt = profile.belt["Brazilian Jiu Jitsu"];
      
      // Process BJJ belt data
      
      // Parse stripes value carefully ensuring it's a number
      let stripeCount = 0;
      
      // First try BJJ-specific stripeCount (priority)
      if (bjjBelt.stripeCount !== undefined) {
        stripeCount = parseInt(bjjBelt.stripeCount, 10);
        // Using BJJ-specific stripe count
      }
      // Also check for 'stripes' in BJJ belt
      else if (bjjBelt.stripes !== undefined) {
        stripeCount = parseInt(bjjBelt.stripes, 10);
      } 
      // Then try root-level stripesCount (should be ignored if BJJ data exists)
      else if (profile.stripesCount !== undefined && !bjjBelt.stripeCount) {
        stripeCount = parseInt(profile.stripesCount, 10);
      }
      // Then try general belt.stripes
      else if (profile.belt.stripes !== undefined) {
        stripeCount = parseInt(profile.belt.stripes, 10);
      }
      
      // Ensure it's a valid number after parsing
      if (isNaN(stripeCount)) {
        stripeCount = 0;
      }
      
      // Ensure the belt info is properly mapped for BJJ
      // Use BJJ-specific belt name/color as the designation
      const beltDesignation = bjjBelt.name || bjjBelt.designation || profile.belt.name || profile.belt.designation || profile.beltDesignation || 'white';
      
      normalizedProfile.belt = {
        ...profile.belt,
        designation: beltDesignation,
        stripes: stripeCount,
        dateObtained: bjjBelt.dateObtained || profile.belt.dateObtained,
        // Make sure promotion history is accessible
        promotionHistory: profile.belt.promotionHistory || [],
        // Include the BJJ-specific data for reference
        "Brazilian Jiu Jitsu": bjjBelt
      };
      
      // Set stripesCount at root level for easy access (matching original app)
      normalizedProfile.stripesCount = stripeCount;
      // Also set beltDesignation at root level
      normalizedProfile.beltDesignation = normalizedProfile.belt.designation;
      
    }
    
    // Profile normalized successfully
    
    return normalizedProfile;
  } catch (error) {
    logger.error('Error fetching profile:', error);
    throw error;
  }
};

// Get profile by username
export const getProfileByUsername = async (username: string): Promise<UserProfile | null> => {
  try {
    // First get the user ID from the username mapping
    const usernameData = await getData(`/usernames/${username}`);
    if (!usernameData || !usernameData.uid) return null;
    
    // Then get the user profile
    return getProfileByUid(usernameData.uid);
  } catch (error) {
    logger.error('Error fetching profile by username:', error);
    throw error;
  }
};

// Check if username is available
export const isUsernameAvailable = async (username: string): Promise<boolean> => {
  try {
    const usernameData = await getData(`/usernames/${username}`);
    return !usernameData;
  } catch (error) {
    logger.error('Error checking username availability:', error);
    throw error;
  }
};

// Update username
export const updateUsername = async (uid: string, newUsername: string): Promise<void> => {
  try {
    // Check if username is available
    const isAvailable = await isUsernameAvailable(newUsername);
    if (!isAvailable) {
      throw new Error('Username is already taken');
    }
    
    // Get current username
    const profile = await getProfileByUid(uid);
    if (!profile) {
      throw new Error('User profile not found');
    }
    
    const oldUsername = profile.username;
    
    // Update username in profile
    await updateData(`/users/${uid}`, { username: newUsername });
    
    // Update username mapping
    const updates: Record<string, any> = {
      [`/usernames/${newUsername}`]: { uid },
    };
    
    // Remove old username mapping if it exists
    if (oldUsername) {
      updates[`/usernames/${oldUsername}`] = null;
    }
    
    await updateMultipleSafe(updates);
  } catch (error) {
    logger.error('Error updating username:', error);
    throw error;
  }
};

// Update profile data
export const updateProfileData = async (uid: string, data: UserProfileUpdateData): Promise<UserProfile> => {
  try {
    // Use data EXACTLY as provided - don't add any extra fields
    const profileData: any = { ...data };

    // Update profile data directly in Firebase like the original app
    await updateData(`/users/${uid}`, profileData);

    // Fetch the full profile to return to Redux
    // This ensures the UI has all fields after the update
    const updatedProfile = await getProfileByUid(uid);

    if (!updatedProfile) {
      throw new Error('Failed to fetch updated profile');
    }

    return updatedProfile;
  } catch (error) {
    logger.error('Error updating profile:', error);
    throw error;
  }
};

// Upload profile photo to storage and return URL
// NOTE: This function only uploads - it does NOT update the profile
// The caller is responsible for updating the profile with the returned URL
export const uploadProfilePhoto = async (uid: string, file: File): Promise<string> => {
  try {
    // Generate a unique filename matching original app pattern
    const fileName = `${uid}-avatar.jpg`;
    const filePath = fileName; // Root of storage, matching original app

    // Upload file to Firebase Storage
    const uploadURL = await uploadFile(filePath, file);

    // Return URL for the caller to save to profile
    return uploadURL;
  } catch (error) {
    logger.error('Error uploading profile photo:', error);
    throw error;
  }
};

// Upload cover photo to storage and return URL with aspect ratio
// NOTE: This function only uploads - it does NOT update the profile
// The caller is responsible for updating the profile with the returned data
export const uploadCoverPhoto = async (uid: string, file: File): Promise<{ url: string; aspectRatio: number }> => {
  try {
    // Generate a unique filename matching original app pattern
    const fileName = `${uid}-cover.jpg`;
    const filePath = fileName; // Root of storage, matching original app

    // Calculate aspect ratio from the image file
    let aspectRatio = 1; // Default fallback
    try {
      aspectRatio = await calculateImageAspectRatio(file);
    } catch (error) {
      logger.warn('Failed to calculate aspect ratio, using default:', error);
    }

    // Upload file to Firebase Storage
    const uploadURL = await uploadFile(filePath, file);

    // Return URL and aspect ratio for the caller to save to profile
    return {
      url: uploadURL,
      aspectRatio: aspectRatio
    };
  } catch (error) {
    logger.error('Error uploading cover photo:', error);
    throw error;
  }
};

/**
 * Upload profile photo from data URL (for native platforms)
 * Returns URL for the caller to save to profile
 */
export const uploadProfilePhotoDataUrl = async (uid: string, dataUrl: string): Promise<string> => {
  try {
    // Extract base64 data and mime type from data URL
    const matches = dataUrl.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
    if (!matches || matches.length !== 3) {
      throw new Error('Invalid data URL format');
    }

    const mimeType = matches[1];
    const base64Data = matches[2];

    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Create File from blob
    const file = new File([blob], 'profile.jpg', { type: mimeType });

    // Use existing upload function which returns URL string
    return await uploadProfilePhoto(uid, file);
  } catch (error) {
    logger.error('Error uploading profile photo from data URL:', error);
    throw error;
  }
};

/**
 * Upload cover photo from data URL (for native platforms)
 * Returns URL and aspect ratio for the caller to save to profile
 */
export const uploadCoverPhotoDataUrl = async (uid: string, dataUrl: string): Promise<{ url: string; aspectRatio: number }> => {
  try {
    logger.info('🔵 [1/6] Starting cover photo upload from data URL...');

    // Calculate aspect ratio directly from data URL before any conversion
    let aspectRatio = 1; // Default fallback
    try {
      logger.info('🔵 [2/6] Calculating aspect ratio...');
      // Create an Image element to load the data URL
      aspectRatio = await new Promise<number>((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => {
          reject(new Error('Aspect ratio calculation timed out after 5s'));
        }, 5000);

        img.onload = () => {
          clearTimeout(timeout);
          const ratio = img.naturalWidth / img.naturalHeight;
          resolve(ratio);
        };
        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load image'));
        };
        img.src = dataUrl;
      });
      logger.info(`✅ [2/6] Calculated aspect ratio: ${aspectRatio}`);
    } catch (error) {
      logger.warn('[2/6] Failed to calculate aspect ratio, using default:', error);
    }

    logger.info('🔵 [3/6] Extracting base64 data...');
    // Extract base64 data and mime type from data URL
    const matches = dataUrl.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
    if (!matches || matches.length !== 3) {
      throw new Error('Invalid data URL format');
    }

    const mimeType = matches[1];
    const base64Data = matches[2];
    logger.info(`✅ [3/6] Extracted base64 data, mime type: ${mimeType}, size: ${base64Data.length} chars`);

    logger.info('🔵 [4/6] Converting base64 to File...');
    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Create File from blob
    const file = new File([blob], 'cover.jpg', { type: mimeType });
    logger.info(`✅ [4/6] Created File object, size: ${file.size} bytes`);

    // Generate filename matching original app pattern
    const fileName = `${uid}-cover.jpg`;
    const filePath = fileName; // Root of storage, matching original app

    logger.info(`🔵 [5/6] Uploading to Firebase Storage at path: ${filePath}...`);
    // Upload file to Firebase Storage
    const uploadURL = await uploadFile(filePath, file);
    logger.info(`✅ [5/6] Upload complete! URL: ${uploadURL.substring(0, 50)}...`);

    logger.info('🔵 [6/6] Returning result...');
    // Return URL and aspect ratio for the caller to save to profile
    return {
      url: uploadURL,
      aspectRatio: aspectRatio
    };
  } catch (error) {
    logger.error('❌ Error uploading cover photo from data URL:', error);
    throw error;
  }
};

// Add or update martial art
export const updateMartialArt = async (
  uid: string, 
  martialArtId: string, 
  data: {
    name: string;
    startDate: number;
    experience: number;
    rank?: string;
    instructor?: string;
  }
): Promise<UserProfile> => {
  try {
    await updateData(`/users/${uid}/martialArts/${martialArtId}`, data);
    
    // Get and return the updated profile
    const updatedProfile = await getProfileByUid(uid);
    if (!updatedProfile) {
      throw new Error('Failed to fetch updated profile');
    }
    
    return updatedProfile;
  } catch (error) {
    logger.error('Error updating martial art:', error);
    throw error;
  }
};

// Remove martial art
export const removeMartialArt = async (uid: string, martialArtId: string): Promise<UserProfile> => {
  try {
    await removeData(`/users/${uid}/martialArts/${martialArtId}`);
    
    // Get and return the updated profile
    const updatedProfile = await getProfileByUid(uid);
    if (!updatedProfile) {
      throw new Error('Failed to fetch updated profile');
    }
    
    return updatedProfile;
  } catch (error) {
    logger.error('Error removing martial art:', error);
    throw error;
  }
};

// This function has been removed as we should not use mock data

// Get user by exact username match
export const getUserByUsername = async (username: string): Promise<UserProfile | null> => {
  try {
    if (!username) {
      return null;
    }
    
    // Query the usernames collection to get the uid
    const usernameRef = ref(database, `usernames/${username}`);
    const usernameSnapshot = await get(usernameRef);
    
    if (!usernameSnapshot.exists()) {
      return null;
    }
    
    const data = usernameSnapshot.val();
    if (!data.uid) {
      return null;
    }
    
    // Get the full profile using the uid
    const profile = await getProfileByUid(data.uid);
    return profile;
  } catch (error) {
    logger.error('Error getting user by username:', error);
    throw error;
  }
};

// Search users by name or username with Firebase (used as fallback when PostgreSQL search fails)
export const searchUsers = async (searchTerm: string, limit: number = 20): Promise<UserSearchResult[]> => {
  try {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }
    
    
    // Remove @ if provided
    let usernameToTry = searchTerm;
    if (usernameToTry.startsWith('@')) {
      usernameToTry = usernameToTry.substring(1);
    }
    
    // Get the current auth user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to search for users');
    }
    
    // Direct Firebase query for usernames
    
    // Query the usernames collection
    const usernamesRef = ref(database, 'usernames');
    const usernamesSnapshot = await get(usernamesRef);
    
    const matches: UserSearchResult[] = [];
    const searchPattern = usernameToTry.toLowerCase();
    
    if (usernamesSnapshot.exists()) {
      // Find usernames that contain the search term
      usernamesSnapshot.forEach(child => {
        const username = child.key?.toLowerCase() || '';
        const userData = child.val();
        
        if (username.includes(searchPattern) && userData.uid) {
          // Add to our results
          matches.push({
            uid: userData.uid,
            id: userData.uid, // Add id field for compatibility
            username: child.key || username, // Keep original casing in the result
            displayName: '', // We'll populate this later if needed
            profilePhotoURL: null,
            isInstructor: false
          });
        }
      });
      
      // Sort by relevance (exact > prefix > substring)
      matches.sort((a, b) => {
        const aUsername = (a.username || '').toLowerCase();
        const bUsername = (b.username || '').toLowerCase();
        
        // Exact match
        if (aUsername === searchPattern && bUsername !== searchPattern) return -1;
        if (bUsername === searchPattern && aUsername !== searchPattern) return 1;
        
        // Prefix match
        if (aUsername.startsWith(searchPattern) && !bUsername.startsWith(searchPattern)) return -1;
        if (bUsername.startsWith(searchPattern) && !aUsername.startsWith(searchPattern)) return 1;
        
        // Alphabetical
        return aUsername.localeCompare(bUsername);
      });
      
      
      // Enhance search results with display names if possible
      const enhancedResults = await Promise.all(
        matches.slice(0, limit).map(async (result) => {
          try {
            // Try to get more profile info
            const profile = await getProfileByUid(result.uid);
            if (profile) {
              result.displayName = profile.displayName || 
                `${profile.firstName} ${profile.lastName}`.trim();
              result.profilePhotoURL = profile.profilePhotoURL || profile.photoURL;
              result.isInstructor = profile.isInstructor || false;
              // Add id field to ensure compatibility with API results
              result.id = result.uid;
            }
          } catch (err) {
            // Just use what we have if profile fetch fails
          }
          return result;
        })
      );
      
      return enhancedResults;
    } else {
      return [];
    }
  } catch (error) {
    logger.error('Error in Firebase search:', error);
    // Don't throw, just return empty results to prevent cascade failures
    return [];
  }
};

// Get user statistics
export const getUserStats = async (uid: string): Promise<UserProfile['stats'] | null> => {
  try {
    const stats = await getData(`/users/${uid}/stats`);
    return stats;
  } catch (error) {
    logger.error('Error fetching user stats:', error);
    throw error;
  }
};

// Update user statistics (typically done by system functions)
export const updateUserStats = async (uid: string, stats: Partial<UserProfile['stats']>): Promise<void> => {
  try {
    await updateData(`/users/${uid}/stats`, stats);
  } catch (error) {
    logger.error('Error updating user stats:', error);
    throw error;
  }
};

// Send friend request - matching original app's manageFriendRequest function
export const sendFriendRequest = async (fromUid: string, toUid: string): Promise<void> => {
  try {
    // Check if already have a relationship
    const existingRelationship = await getData(`/users/${fromUid}/friends/${toUid}`);
    if (existingRelationship) {
      if (existingRelationship === 'accepted') {
        throw new Error('Already friends with this user');
      } else if (existingRelationship === 'request-sent') {
        throw new Error('Friend request already sent');
      } else if (existingRelationship === 'request-received') {
        throw new Error('This user already sent you a friend request');
      }
    }

    // Update both users' friends lists
    // Write 1: Set our own friend status (uses parent rule: $uid === auth.uid)
    try {
      await setData(`/users/${fromUid}/friends/${toUid}`, 'request-sent');
      logger.info(`[sendFriendRequest] Set request-sent on own profile for ${toUid}`);
    } catch (ownWriteError) {
      logger.error(`[sendFriendRequest] Failed to write own friend status:`, ownWriteError);
      throw ownWriteError;
    }

    // Write 2: Set recipient's friend status (uses child rule: auth.uid === $friendUid)
    try {
      await setData(`/users/${toUid}/friends/${fromUid}`, 'request-received');
      logger.info(`[sendFriendRequest] Set request-received on recipient profile for ${fromUid}`);
    } catch (recipientWriteError) {
      logger.error(`[sendFriendRequest] Failed to write recipient friend status:`, recipientWriteError);
      // Rollback Write 1 to prevent asymmetric state
      try {
        await setData(`/users/${fromUid}/friends/${toUid}`, null);
        logger.info(`[sendFriendRequest] Rolled back own friend status after Write 2 failure`);
      } catch (rollbackErr) {
        logger.error(`[sendFriendRequest] Rollback failed:`, rollbackErr);
      }
      throw recipientWriteError;
    }

    // Notification is handled by the 'friendsChanged' Cloud Function
    // which triggers on /users/{userUID}/friends/{requesterUID} onWrite
  } catch (error: any) {
    const errorMsg = error?.message || error?.code || JSON.stringify(error);
    logger.error(`[sendFriendRequest] Error: ${errorMsg}`, error);
    throw error;
  }
};

// Accept friend request - matching original app's manageFriendRequest function
export const acceptFriendRequest = async (currentUid: string, requestorUid: string): Promise<void> => {
  try {
    // Check current relationship status (but be lenient like original app)
    const currentStatus = await getData(`/users/${currentUid}/friends/${requestorUid}`);

    // Log the current status for debugging
    logger.info(`[acceptFriendRequest] Current status for ${requestorUid}: ${JSON.stringify(currentStatus)}`);

    // Be lenient with status check - original app doesn't do strict status checking
    // Only reject if already friends or already declined
    if (currentStatus === 'accepted' || currentStatus === true) {
      throw new Error('Already friends with this user');
    }
    if (currentStatus === 'declined') {
      throw new Error('Friend request was previously declined');
    }
    if (currentStatus === 'request-sent') {
      // They sent us a request but we also sent them one - just accept!
      logger.info('[acceptFriendRequest] Both users sent requests, accepting anyway');
    }

    // Update both users to 'accepted' status (matching original app)
    // Use the same multi-path update pattern as original app
    const updates: Record<string, any> = {
      [`/users/${currentUid}/friends/${requestorUid}`]: 'accepted',
      [`/users/${requestorUid}/friends/${currentUid}`]: 'accepted'
    };

    // Try updateMultipleSafe first (uses individual writes)
    try {
      await updateMultipleSafe(updates);
    } catch (updateError: any) {
      // If updateMultipleSafe fails, try updating just our own record
      // This ensures at least partial success
      logger.error('[acceptFriendRequest] Multi-update failed, trying single update:', updateError);

      // Update our own friend status at minimum
      await setData(`/users/${currentUid}/friends/${requestorUid}`, 'accepted');

      // Log that we couldn't update the other user
      logger.warn(`[acceptFriendRequest] Could not update requestor's friends list. ` +
        `They may need to accept from their side or sync will fix it.`);
    }
    
    // Check for 10 for 10 perk after adding friend
    try {
      // Get updated friend count for current user (only count "accepted" friends)
      const currentUserFriends = await getData(`/users/${currentUid}/friends`);
      const currentFriendCount = currentUserFriends 
        ? Object.values(currentUserFriends).filter(status => status === "accepted" || status === true).length 
        : 0;
      await perksService.check10For10Perk(currentUid, currentFriendCount);
      
      // Also check for requestor (only count "accepted" friends)
      const requestorFriends = await getData(`/users/${requestorUid}/friends`);
      const requestorFriendCount = requestorFriends 
        ? Object.values(requestorFriends).filter(status => status === "accepted" || status === true).length 
        : 0;
      await perksService.check10For10Perk(requestorUid, requestorFriendCount);
    } catch (perkError) {
      logger.error('Error checking perks after friend acceptance:', perkError);
      // Don't fail the friend acceptance if perk checking fails
    }
    
    // Notification is handled by the 'friendsChanged' Cloud Function
    // which triggers on /users/{userUID}/friends/{requesterUID} onWrite
  } catch (error) {
    logger.error('Error accepting friend request:', error);
    throw error;
  }
};

// Decline friend request - matching original app's manageFriendRequest function
export const declineFriendRequest = async (currentUid: string, requestorUid: string): Promise<void> => {
  try {
    // Check current relationship status
    const currentStatus = await getData(`/users/${currentUid}/friends/${requestorUid}`);
    if (currentStatus !== 'request-received') {
      throw new Error('No pending friend request to decline');
    }

    // Update statuses (matching original app)
    const updates: Record<string, any> = {
      [`/users/${currentUid}/friends/${requestorUid}`]: 'declined',
      [`/users/${requestorUid}/friends/${currentUid}`]: 'request-sent' // They still have request-sent status
    };

    await updateMultipleSafe(updates);
  } catch (error) {
    logger.error('Error declining friend request:', error);
    throw error;
  }
};

// Undo a previously declined friend request - accepts the user as a friend
export const undoDeclineFriendRequest = async (currentUid: string, declinedUid: string): Promise<void> => {
  try {
    // Check current relationship status
    const currentStatus = await getData(`/users/${currentUid}/friends/${declinedUid}`);

    logger.info(`[undoDeclineFriendRequest] Current status for ${declinedUid}: ${JSON.stringify(currentStatus)}`);

    // Only allow undo if status is 'declined'
    if (currentStatus !== 'declined') {
      throw new Error('This user was not previously declined');
    }

    // Update both users to 'accepted' status
    // The other user still has 'request-sent', so we're essentially accepting their request
    const updates: Record<string, any> = {
      [`/users/${currentUid}/friends/${declinedUid}`]: 'accepted',
      [`/users/${declinedUid}/friends/${currentUid}`]: 'accepted'
    };

    try {
      await updateMultipleSafe(updates);
    } catch (updateError: any) {
      // If multi-update fails, try updating just our own record
      logger.error('[undoDeclineFriendRequest] Multi-update failed, trying single update:', updateError);
      await setData(`/users/${currentUid}/friends/${declinedUid}`, 'accepted');
      logger.warn(`[undoDeclineFriendRequest] Could not update other user's friends list.`);
    }

    // Notification is handled by the 'friendsChanged' Cloud Function

    logger.info(`[undoDeclineFriendRequest] Successfully accepted previously declined user ${declinedUid}`);
  } catch (error) {
    logger.error('Error undoing declined friend request:', error);
    throw error;
  }
};

// Remove friend
export const removeFriend = async (currentUid: string, friendUid: string): Promise<void> => {
  try {
    const updates: Record<string, any> = {
      [`/users/${currentUid}/friends/${friendUid}`]: null,
      [`/users/${friendUid}/friends/${currentUid}`]: null
    };
    
    await updateMultipleSafe(updates);
  } catch (error) {
    logger.error('Error removing friend:', error);
    throw error;
  }
};

// Get friends list
export const getFriends = async (uid: string): Promise<UserSearchResult[]> => {
  try {
    logger.info(`[getFriends] Fetching friends for user: ${uid}`);
    const friendsPath = `/users/${uid}/friends`;
    
    // Add retry logic for permission errors
    let friendsData;
    try {
      friendsData = await getData(friendsPath);
    } catch (error: any) {
      if (error?.code === 'PERMISSION_DENIED' || error?.message?.includes('Permission denied')) {
        logger.warn(`[getFriends] Permission denied on first attempt, retrying...`);
        // Wait a bit for auth state to propagate
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
          friendsData = await getData(friendsPath);
        } catch (retryError) {
          logger.error(`[getFriends] Retry failed for path ${friendsPath}:`, retryError);
          // Return empty array instead of throwing to prevent feed from breaking
          return [];
        }
      } else {
        throw error;
      }
    }
    
    if (!friendsData) {
      logger.info(`[getFriends] No friends data found for user ${uid}`);
      return [];
    }
    
    // Only include accepted friends (support both boolean true and "accepted" status)
    const friendUids = Object.entries(friendsData)
      .filter(([uid, status]) => status === "accepted" || status === true)
      .map(([uid]) => uid);
    logger.info(`[getFriends] Found ${friendUids.length} accepted friends for user ${uid}`);
    
    const friends: UserSearchResult[] = [];
    
    // Fetch all friend profiles in parallel
    const profilePromises = friendUids.map(friendUid => 
      getProfileByUid(friendUid).catch(error => {
        logger.error(`Failed to fetch profile for ${friendUid}:`, error);
        return null;
      })
    );
    
    const friendProfiles = await Promise.all(profilePromises);
    
    // Process the results
    friendProfiles.forEach((friendProfile, index) => {
      if (friendProfile) {
        const friendUid = friendUids[index];
        friends.push({
          uid: friendUid,
          id: friendUid, // Add id field for compatibility
          username: friendProfile.username,
          displayName: friendProfile.displayName || `${friendProfile.firstName} ${friendProfile.lastName}`,
          profilePhotoURL: friendProfile.profilePhotoURL || friendProfile.photoURL,
          isInstructor: friendProfile.isInstructor,
          isPeakSub: friendProfile.isPeakSub
        });
      }
    });
    
    return friends;
  } catch (error) {
    logger.error('Error fetching friends:', error);
    throw error;
  }
};

// Get complete friend profiles (with full profile data, not just search results)
export const getFullFriendProfiles = async (uid: string): Promise<Record<string, UserProfile>> => {
  try {
    logger.info(`[getFullFriendProfiles] Fetching full friend profiles for user: ${uid}`);
    const friendsPath = `/users/${uid}/friends`;
    
    // Add retry logic for permission errors
    let friendsData;
    try {
      friendsData = await getData(friendsPath);
    } catch (error: any) {
      if (error?.code === 'PERMISSION_DENIED' || error?.message?.includes('Permission denied')) {
        logger.warn(`[getFullFriendProfiles] Permission denied on first attempt, retrying...`);
        // Wait a bit for auth state to propagate
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
          friendsData = await getData(friendsPath);
        } catch (retryError) {
          logger.error(`[getFullFriendProfiles] Retry failed for path ${friendsPath}:`, retryError);
          // Return empty object instead of throwing to prevent feed from breaking
          return {};
        }
      } else {
        throw error;
      }
    }
    
    if (!friendsData) {
      logger.info(`[getFullFriendProfiles] No friends data found for user ${uid}`);
      return {};
    }
    
    const friendUids = Object.keys(friendsData);
    logger.info(`[getFullFriendProfiles] Found ${friendUids.length} friends for user ${uid}`);
    
    const friendProfiles: Record<string, UserProfile> = {};
    
    // Fetch all friend profiles in parallel
    const profilePromises = friendUids.map(friendUid => 
      getProfileByUid(friendUid)
        .then(profile => ({ uid: friendUid, profile }))
        .catch(error => {
          logger.error(`Failed to fetch profile for ${friendUid}:`, error);
          return { uid: friendUid, profile: null };
        })
    );
    
    const results = await Promise.all(profilePromises);
    
    // Build the profiles object
    results.forEach(({ uid, profile }) => {
      if (profile) {
        friendProfiles[uid] = profile;
      }
    });
    
    return friendProfiles;
  } catch (error) {
    logger.error('Error fetching full friend profiles:', error);
    throw error;
  }
};

/**
 * Get user profiles by UIDs - similar to the original app's getUsersProfiles
 * Used to fetch profiles for users who aren't friends but have posts in the feed
 */
export const getUserProfilesByUids = async (uids: string[]): Promise<Record<string, UserProfile>> => {
  try {
    if (!uids.length) return {};
    
    const profiles: Record<string, UserProfile> = {};
    
    // Fetch all user profiles in parallel
    const profilePromises = uids.map(uid => 
      getProfileByUid(uid)
        .then(profile => ({ uid, profile }))
        .catch(error => {
          logger.error(`Failed to fetch profile for ${uid}:`, error);
          return { uid, profile: null };
        })
    );
    
    const results = await Promise.all(profilePromises);
    
    // Build the profiles object
    results.forEach(({ uid, profile }) => {
      if (profile) {
        profiles[uid] = profile;
      }
    });
    
    return profiles;
  } catch (error) {
    logger.error('Error fetching user profiles by UIDs:', error);
    throw error;
  }
};

/**
 * Gets a username by UID - optimized function to just fetch the username
 * @param uid User ID to look up
 * @returns Username or null if not found
 */
export const getUsernameByUid = async (uid: string): Promise<string | null> => {
  try {
    // First try to get from the users collection
    const userData = await getData(`/users/${uid}`);
    if (userData && userData.username) {
      return userData.username;
    }
    
    // If no username found, use a simplified fallback
    return 'User-' + uid.substring(0, 5);
  } catch (error) {
    logger.error(`Error fetching username for UID ${uid}:`, error);
    return 'User-' + uid.substring(0, 5);
  }
};

// Get friend requests - reads from /users/{uid}/friends path (matching original app)
// Returns sent, received, and declined requests with user profile data
export const getFriendRequests = async (uid: string): Promise<{
  sent: Array<{
    id: string;
    recipientId: string;
    senderId: string;
    status: 'pending';
    createdAt: number;
    recipientProfile?: any;
  }>;
  received: Array<{
    id: string;
    senderId: string;
    recipientId: string;
    status: 'pending';
    createdAt: number;
    senderProfile?: any;
  }>;
  declined: Array<{
    id: string;
    senderId: string;
    recipientId: string;
    status: 'declined';
    createdAt: number;
    userProfile?: any;
  }>;
  // Also include legacy format for FriendRequestsList component
  [key: string]: any;
}> => {
  try {
    // Read from the CORRECT path: /users/{uid}/friends (not /friendRequests)
    const friendsData = await getData(`/users/${uid}/friends`);

    if (!friendsData) {
      return { sent: [], received: [], declined: [] };
    }

    // Separate into sent, received, and declined based on status strings
    const sentIds: string[] = [];
    const receivedIds: string[] = [];
    const declinedIds: string[] = [];

    Object.entries(friendsData).forEach(([friendId, status]) => {
      if (status === 'request-sent') {
        sentIds.push(friendId);
      } else if (status === 'request-received') {
        receivedIds.push(friendId);
      } else if (status === 'declined') {
        declinedIds.push(friendId);
      }
    });

    logger.info(`[getFriendRequests] Found ${receivedIds.length} received, ${sentIds.length} sent, ${declinedIds.length} declined requests`);

    // Fetch profile data for all users in parallel (including declined)
    const allIds = [...sentIds, ...receivedIds, ...declinedIds];
    const profilePromises = allIds.map(userId =>
      getProfileByUid(userId)
        .then(profile => ({ userId, profile }))
        .catch(error => {
          logger.error(`Failed to fetch profile for ${userId}:`, error);
          return { userId, profile: null };
        })
    );

    const profileResults = await Promise.all(profilePromises);
    const profileMap: Record<string, any> = {};
    profileResults.forEach(({ userId, profile }) => {
      if (profile) {
        profileMap[userId] = profile;
      }
    });

    // Build sent array
    const sent = sentIds.map(recipientId => ({
      id: recipientId,
      recipientId,
      senderId: uid,
      status: 'pending' as const,
      createdAt: Date.now(), // Original app doesn't store request date in friends object
      recipientProfile: profileMap[recipientId] ? {
        uid: recipientId,
        photoURL: profileMap[recipientId].profilePhotoURL || profileMap[recipientId].photoURL,
        displayName: profileMap[recipientId].displayName ||
          `${profileMap[recipientId].firstName || ''} ${profileMap[recipientId].lastName || ''}`.trim(),
        username: profileMap[recipientId].username,
        firstName: profileMap[recipientId].firstName,
        lastName: profileMap[recipientId].lastName,
        isInstructor: profileMap[recipientId].isInstructor
      } : undefined
    }));

    // Build received array
    const received = receivedIds.map(senderId => ({
      id: senderId,
      senderId,
      recipientId: uid,
      status: 'pending' as const,
      createdAt: Date.now(),
      senderProfile: profileMap[senderId] ? {
        uid: senderId,
        photoURL: profileMap[senderId].profilePhotoURL || profileMap[senderId].photoURL,
        displayName: profileMap[senderId].displayName ||
          `${profileMap[senderId].firstName || ''} ${profileMap[senderId].lastName || ''}`.trim(),
        username: profileMap[senderId].username,
        firstName: profileMap[senderId].firstName,
        lastName: profileMap[senderId].lastName,
        isInstructor: profileMap[senderId].isInstructor
      } : undefined
    }));

    // Build declined array - users whose requests you previously declined
    const declined = declinedIds.map(senderId => ({
      id: senderId,
      senderId,
      recipientId: uid,
      status: 'declined' as const,
      createdAt: Date.now(),
      userProfile: profileMap[senderId] ? {
        uid: senderId,
        photoURL: profileMap[senderId].profilePhotoURL || profileMap[senderId].photoURL,
        displayName: profileMap[senderId].displayName ||
          `${profileMap[senderId].firstName || ''} ${profileMap[senderId].lastName || ''}`.trim(),
        username: profileMap[senderId].username,
        firstName: profileMap[senderId].firstName,
        lastName: profileMap[senderId].lastName,
        isInstructor: profileMap[senderId].isInstructor
      } : undefined
    }));

    // Also build legacy format for FriendRequestsList component compatibility
    // This creates { [senderId]: { status: 'pending', fromUser: {...} } }
    const legacyFormat: Record<string, any> = {};
    received.forEach(request => {
      legacyFormat[request.senderId] = {
        from: request.senderId,
        date: request.createdAt,
        status: 'pending',
        fromUser: request.senderProfile ? {
          uid: request.senderId,
          id: request.senderId,
          username: request.senderProfile.username,
          displayName: request.senderProfile.displayName,
          photoURL: request.senderProfile.photoURL,
          profilePhotoURL: request.senderProfile.photoURL,
          isInstructor: request.senderProfile.isInstructor
        } : undefined
      };
    });

    // Return combined object with both formats
    return {
      sent,
      received,
      declined,
      ...legacyFormat
    };
  } catch (error) {
    logger.error('Error fetching friend requests:', error);
    throw error;
  }
};

// Track user activity
export const trackUserActivity = async (uid: string): Promise<void> => {
  try {
    await updateData(`/users/${uid}`, {
      lastActive: Date.now()
    });
  } catch (error) {
    logger.error('Error tracking user activity:', error);
    throw error;
  }
};

// Subscribe to profile changes
export const subscribeToProfile = (uid: string, callback: (profile: UserProfile | null) => void): (() => void) => {
  const unsubscribe = subscribeToData(`/users/${uid}`, (data) => {
    if (!data) {
      callback(null);
      return;
    }
    
    callback({
      uid,
      ...data
    });
  });
  
  return unsubscribe;
};

// Subscribe to user stats changes
export const subscribeToUserStats = (uid: string, callback: (stats: UserProfile['stats'] | null) => void): (() => void) => {
  const unsubscribe = subscribeToData(`/users/${uid}/stats`, (data) => {
    callback(data);
  });
  
  return unsubscribe;
};

// Subscribe to friends changes
export const subscribeToFriends = (uid: string, callback: (friendIds: string[]) => void): (() => void) => {
  const unsubscribe = subscribeToData(`/users/${uid}/friends`, (data) => {
    if (!data) {
      callback([]);
      return;
    }
    
    callback(Object.keys(data));
  });
  
  return unsubscribe;
};

// Subscribe to friend requests
export const subscribeToFriendRequests = (uid: string, callback: (requests: Record<string, any>) => void): (() => void) => {
  const unsubscribe = subscribeToData(`/users/${uid}/friendRequests`, (data) => {
    if (!data) {
      callback({});
      return;
    }
    
    callback(data);
  });
  
  return unsubscribe;
};

/**
 * Updates the current user's profile - matches original app's API
 * @param updateObject An object containing new data to be updated on the user's profile
 */
export const updateCurrentUserProfile = async (updateObject: Partial<UserProfileUpdateData>): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    
    await updateProfileData(currentUser.uid, updateObject);
  } catch (error) {
    logger.error('Error updating current user profile:', error);
    throw error;
  }
};

/**
 * Updates a user's Marune Peak subscription status
 * Matches original app: user.service.js - updateSubStatus
 * @param {boolean} newStatus The new subscription status received from the App Store/Play Store
 */
export const updateSubStatus = async (newStatus: boolean): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    
    // Update the user's isPeakSub field to match original app
    await updateData(`/users/${currentUser.uid}`, {
      isPeakSub: newStatus
    });
  } catch (error) {
    logger.error('Error updating subscription status:', error);
    throw error;
  }
};

/**
 * Helper function to match original app's storage path for profile photos
 * Original app uses just '{uid}-avatar.jpg' in the root of storage
 */
export const uploadProfilePhotoOriginalFormat = async (
  uid: string,
  source: string | File
): Promise<string> => {
  try {
    const targetUid = uid || auth.currentUser?.uid;
    if (!targetUid) {
      throw new Error('No user ID provided for profile photo upload');
    }

    // If the source is a File we can upload it directly.
    if (typeof File !== 'undefined' && source instanceof File) {
      const url = await uploadProfilePhoto(targetUid, source);
      await updateData(`/users/${targetUid}`, {
        photoURL: url,
        profilePhotoURL: url
      });
      return url;
    }

    if (typeof source === 'string') {
      // Data URL path (most common for native/web bridges)
      if (source.startsWith('data:')) {
        const url = await uploadProfilePhotoDataUrl(targetUid, source);
        await updateData(`/users/${targetUid}`, {
          photoURL: url,
          profilePhotoURL: url
        });
        return url;
      }

      // Remote HTTP(S) resource – fetch, then upload
      if (source.startsWith('http://') || source.startsWith('https://')) {
        const response = await fetch(source);
        if (!response.ok) {
          throw new Error(`Failed to download profile image: ${response.status}`);
        }
        const blob = await response.blob();
        const fileName = `${targetUid}-avatar.jpg`;
        const file =
          typeof File !== 'undefined'
            ? new File([blob], fileName, { type: blob.type || 'image/jpeg' })
            : (blob as unknown as File);
        const url = await uploadProfilePhoto(targetUid, file);
        await updateData(`/users/${targetUid}`, {
          photoURL: url,
          profilePhotoURL: url
        });
        return url;
      }
    }

    throw new Error('Unsupported profile photo source format');
  } catch (error) {
    logger.error('Error uploading profile photo in original format:', error);
    throw error;
  }
};

/**
 * Updates the user's push notification token
 * Matches original app: notifications.service.js - checkNotificationsStatus
 * @param {string} pushToken The FCM token for push notifications
 */
export const updatePushToken = async (pushToken: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    
    await updateData(`/users/${currentUser.uid}`, {
      pushToken
    });
  } catch (error) {
    logger.error('Error updating push token:', error);
    throw error;
  }
};

/**
 * Clears the user's push notification token (on logout)
 * Used when user logs out to prevent notifications being sent to logged out device
 */
export const clearPushToken = async (): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      // Already logged out, nothing to do
      return;
    }
    
    await updateData(`/users/${currentUser.uid}`, {
      pushToken: null
    });
  } catch (error) {
    logger.error('Error clearing push token:', error);
    // Don't throw on logout - this is not critical
  }
};

/**
 * Updates the user's last app usage and platform information
 * Used for analytics and tracking active users
 * @param {string} platform The platform/OS (ios, android, web)
 */
export const updateLastUseAndPlatform = async (platform: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    
    await updateData(`/users/${currentUser.uid}`, {
      lastAppStart: Date.now(),
      deviceOS: platform
    });
  } catch (error) {
    logger.error('Error updating last use and platform:', error);
    // Don't throw - this is analytics and shouldn't break the app
  }
};
